<template>
  <NavMenu :target-el :title="t('invite_others')" @close="emit('close')">
    <div class="share-modal-body" ref="refRoot" tabindex="0">
      <!-- <Tabs :tabs="tabs" :activeTab="activeTab" size="small" @tabClick="handleTabClick" /> -->
      <AppTab :tabs v-model="activeTab" />

      <div v-if="activeTab === 'guest'" class="body-container">
        <div class="container-qr">
          <div class="qr-image-wrapper">
            <img class="qr-image" :src="guestQrCodeImageUrl" />
          </div>
          <div class="qr-link-container">
            <p class="qr-link-text small line-btn m-btn">{{ guestLinkUrl }}</p>
            <button
              class="button-secondary button-medium button-full-width"
              :class="{ done: isGuestCopied }"
              @click="handleCopy('GUEST')"
            >
              {{ isGuestCopied ? $t('copy_completed') : $t('urlcopy') }}
            </button>
          </div>
        </div>
      </div>

      <div v-else class="body-container">
        <div class="container-qr">
          <div class="qr-image-wrapper">
            <img class="qr-image" :src="hostQrCodeImageUrl" />
          </div>
          <div class="qr-link-container">
            <p class="small line-btn m-btn qr-link-text">{{ hostLinkUrl }}</p>
            <button
              class="button-secondary button-medium button-full-width"
              :class="{ done: isEmployeeCopied }"
              @click="handleCopy('EMPLOYEE')"
            >
              {{ isEmployeeCopied ? $t('copy_completed') : $t('urlcopy') }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </NavMenu>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import AppTab from '@/components/domains/mobile/common/AppTab.vue';
import NavMenu from '@/components/shared/composite/NavMenu.vue';
import { useKeyPress } from '@/composables/shared/useKeyPress';
import { useCallStore } from '@/stores/call';
import { Nullable } from '@/types/common';

const { t } = useI18n();
const callStore = useCallStore();

defineProps<{
  hostQrCodeImageUrl: string;
  hostLinkUrl: string;
  guestQrCodeImageUrl: string;
  guestLinkUrl: string;
  targetEl: Nullable<HTMLElement>;
}>();

const emit = defineEmits(['copy', 'close']);

const refRoot = ref<HTMLElement | null>(null);
const activeTab = ref('guest');
const isEmployeeCopied = ref(false);
const isGuestCopied = ref(false);

const tabs = computed(() => {
  const isGuest = callStore.qrCodeInfo.qrCodeType.split('_').pop() === 'GUEST';
  return isGuest
    ? [{ label: t('for_guest'), value: 'guest' }]
    : [
        { label: t('for_guest'), value: 'guest' },
        { label: t('for_host'), value: 'host' },
      ];
});

const handleTabClick = (index) => {
  activeTab.value = index;
};

const handleCopy = (type) => {
  emit('copy', type);
  if (type === 'EMPLOYEE') {
    isEmployeeCopied.value = true;
    setTimeout(() => {
      isEmployeeCopied.value = false;
    }, 10000);
  } else {
    isGuestCopied.value = true;
    setTimeout(() => {
      isGuestCopied.value = false;
    }, 10000);
  }
};

const closeModal = () => {
  emit('close');
};

useKeyPress(refRoot, {
  Escape: () => {
    closeModal();
  },
});

onMounted(() => {
  refRoot.value?.focus();
});
</script>

<style scoped lang="scss">
.share-modal-body {
  display: flex;
  flex-direction: column;
}

.body-container {
  display: flex;
  width: 100%;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: 0.25rem;
}

.container-qr {
  width: 15rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  border-radius: 8px;
}

.qr-image-wrapper {
  border-radius: 8px;
  overflow: hidden;
  display: inline-block;
  background: #fff;
  margin-top: $size-s;
}

.qr-image {
  width: 100%;
  height: auto;
  border-radius: 8px;
  mix-blend-mode: multiply;
}

.qr-link-container {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.qr-link-text {
  display: block;
  width: 100%;
  padding: 0.5rem 1rem;
  align-items: center;
  justify-content: space-between;
  text-align: left;
  border-radius: 8px;
  border: 1px solid var(--border-default);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  background: #fff;
}

::v-deep(.tab-button) {
  width: 50%;
}

@media (max-width: 480px) {
  .container-qr {
    width: 100%;
    .qr-image {
      max-width: 240px;
    }
  }
}
</style>
